// BUILD-042 My HR local walkthrough — "simulate each view for me" (2026-10-07).
//
// VISUAL ROLE SIMULATION ONLY. This toggle changes what this one browser tab RENDERS on the
// SAME My HR screen — it never changes any real permission, grant, or server authorization.
// Matches the existing precedent already accepted for this BUILD: the synthetic preview's own
// viewer-role selector (preview/data.ts's PREVIEW_VIEWER_ROLES) and the Correction Requests
// reviewer's no-self-approval design — both already established that role simulation is
// acceptable as VISUAL EVIDENCE ONLY, never as proof of an actual authorization decision.
//
// Why this exists: HR Administration already has its own real page
// (/hr/admin/employees/:id) for reviewing another Employee's record as an admin. This toggle is
// NOT a replacement for that — it exists so Chris can see, on THIS SAME My HR screen he's
// already reviewing, what changes between "I am the employee looking at my own record" and
// "I am HR Administration reviewing this record" without switching screens or logging in as a
// second person. The two things that actually differ are: (1) whether a governed-fact card
// offers "Request a correction" (employee) or a reviewer view of any pending request plus a
// pointer to the real admin edit path where one exists (HR Administrator), and (2) whether a
// submitted License can be moved to Verified (HR Administrator only — an employee can never
// verify their own submission, per the governing decision).
import { createContext, useContext, useState, type ReactNode } from 'react';

export type MyHrViewerRole = 'employee' | 'hrAdmin';

// A fixed synthetic reviewer identity, distinct from the employee whose record is being viewed,
// so no-self-approval (CorrectionRequestsPanel's canApprove) is never accidentally satisfied by
// viewer-id coincidence while simulating "HR Administrator" for your own record.
export const SIMULATED_HR_ADMIN_ID = 'simulated-hr-admin-reviewer';
export const SIMULATED_HR_ADMIN_NAME = 'Dana HR Admin (synthetic)';

interface MyHrViewerRoleContextValue {
  role: MyHrViewerRole;
  setRole: (role: MyHrViewerRole) => void;
}

const MyHrViewerRoleContext = createContext<MyHrViewerRoleContextValue | null>(null);

export function MyHrViewerRoleProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<MyHrViewerRole>('employee');
  return <MyHrViewerRoleContext.Provider value={{ role, setRole }}>{children}</MyHrViewerRoleContext.Provider>;
}

export function useMyHrViewerRole(): MyHrViewerRoleContextValue {
  const ctx = useContext(MyHrViewerRoleContext);
  if (!ctx) throw new Error('useMyHrViewerRole must be used within a MyHrViewerRoleProvider (see MyHrTab.tsx)');
  return ctx;
}

/** Compact segmented control for the top HEADER bar (AppLayout.tsx) — moved out of the My HR
 *  page body per Chris's request so it never affects page layout/scroll, living instead in
 *  global chrome next to the account menu, visible everywhere, not just on My HR. Same
 *  underlying context/state as before; only where it renders changed. */
export function HeaderViewerRoleToggle() {
  const { role, setRole } = useMyHrViewerRole();
  return (
    // Reconciliation (2026-10-07, R3): the "visual simulation only" explanation previously lived
    // ONLY in this element's `title` attribute (a hover tooltip a signed-in user may never
    // trigger). The visible "Viewing as:" label is kept, but the explanation itself must be
    // visible, not hover-only — see the sr-only text appended below for assistive-tech parity
    // and the always-visible helper text under the control.
    <div className="hidden sm:flex flex-col items-end gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-gray-400">Viewing as:</span>
        <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
          <button
            type="button"
            onClick={() => setRole('employee')}
            aria-pressed={role === 'employee'}
            className={`text-[11px] px-2.5 py-1 ${role === 'employee' ? 'bg-[#4AABDE] text-white' : 'text-gray-300 hover:bg-white/10'}`}
          >
            Employee
          </button>
          <button
            type="button"
            onClick={() => setRole('hrAdmin')}
            aria-pressed={role === 'hrAdmin'}
            className={`text-[11px] px-2.5 py-1 ${role === 'hrAdmin' ? 'bg-[#4AABDE] text-white' : 'text-gray-300 hover:bg-white/10'}`}
          >
            HR Admin
          </button>
        </div>
      </div>
      <span className="text-[10px] text-gray-400 leading-tight">Visual preview only — not a real permission change</span>
    </div>
  );
}
